import { motion } from "framer-motion";
import {
  Crown, Brain, Heart, Flag, Compass, TrendingUp, MessageSquare, Shield, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { ScoreBar } from "./ScoreBar";
import { useLang } from "../i18n/LangContext";

const BIG5_KEYS = ["openness", "conscientiousness", "extraversion", "agreeableness", "neuroticism"];

export function PremiumReportContent({ doc, lang: forcedLang }) {
  const ctx = useLang();
  const lang = forcedLang || ctx.lang;
  const t = (k) => ctx.t(k);
  const r = doc.report || {};
  const p = r.personality || {};
  const a = r.attachment_style || {};
  const c = r.compatibility || {};
  const lt = r.long_term_prediction || {};
  const s = r.approach_strategy || {};

  return (
    <div className="space-y-6" data-testid="premium-report-content">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="dc-card relative overflow-hidden p-8">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-gradient-to-br from-[#ff4458]/25 to-transparent blur-3xl" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#fdb022]/40 bg-[#fdb022]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#fdb022]">
              <Crown className="h-3.5 w-3.5" />{t("premiumBadge")}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{r.title || "Premium Report"}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#a8adbd]">{r.summary}</p>
            {(r.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="report-tags">
                {(r.tags || []).map((tag, i) => (
                  <span key={i} className="rounded-full bg-[#222636] px-2.5 py-1 text-xs text-[#a8adbd]">{tag}</span>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-center rounded-3xl border border-[#222636] bg-[#0f1117] px-8 py-5" data-testid="premium-overall-score">
            <div className="font-display text-5xl font-bold tracking-tighter dc-gradient-text">
              {c.score ?? doc.overall_score ?? 0}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wider text-[#a8adbd]">{t("compatibility")}</div>
          </div>
        </div>
      </motion.div>

      {/* Personality */}
      <Section icon={<Brain className="h-5 w-5 text-[#ff4458]" />} title={t("personality")} testId="personality-section">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <KV label={t("mbti")} value={p.mbti_guess} highlight />
          <KV label={t("loveLanguage")} value={p.love_language} />
          <KV label={t("communicationStyle")} value={p.communication_style} />
          <KV label={t("coreValues")} value={(p.core_values || []).join(", ")} />
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[#dcdde6]">{p.description}</p>
        <div className="mt-6">
          <div className="mb-3 text-xs uppercase tracking-wider text-[#a8adbd]">{t("bigFive")}</div>
          <div className="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
            {BIG5_KEYS.map(k => (
              <ScoreBar key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} value={(p.big_five || {})[k]} testId={`big5-${k}`} />
            ))}
          </div>
        </div>
      </Section>

      {/* Attachment */}
      <Section icon={<Heart className="h-5 w-5 text-[#ff4458]" fill="#ff4458" />} title={t("attachmentStyle")} testId="attachment-section">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="font-display text-3xl font-bold uppercase dc-gradient-text" data-testid="attachment-type">{a.type || "—"}</span>
          <span className="text-sm text-[#a8adbd]">{t("confidence")}: {a.confidence ?? 0}%</span>
        </div>
        <KV label={t("evidence")} value={a.evidence} block />
        <KV label={t("implications")} value={a.implications} block />
      </Section>

      {/* Flags */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Section icon={<CheckCircle2 className="h-5 w-5 text-[#32d583]" />} title={t("greenFlags")} testId="green-flags">
          <List items={r.green_flags} tone="good" />
        </Section>
        <Section icon={<Flag className="h-5 w-5 text-[#fd267a]" />} title={t("redFlags")} testId="red-flags">
          <List items={r.red_flags} tone="bad" />
        </Section>
      </div>

      {/* Compatibility */}
      <Section icon={<Compass className="h-5 w-5 text-[#fdb022]" />} title={t("compatibility")} testId="compatibility-section" accent>
        <ScoreBar label={t("score_chemistry")} value={c.score} testId="compat-score-bar" />
        <p className="mt-4 text-sm leading-relaxed text-[#dcdde6]">{c.verdict}</p>
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-[#a8adbd]">{t("strengths")}</div>
            <List items={c.strengths} tone="good" />
          </div>
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-[#a8adbd]">{t("frictionPoints")}</div>
            <List items={c.friction_points} tone="bad" />
          </div>
        </div>
      </Section>

      {/* Long-term prediction */}
      <Section icon={<TrendingUp className="h-5 w-5 text-[#ff4458]" />} title={t("longTermPrediction")} testId="longterm-section">
        <div className="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
          <ScoreBar label={t("relationshipPotential")} value={lt.relationship_potential} testId="lt-relationship" />
          <ScoreBar label={t("shortFlingLikelihood")} value={lt.short_fling_likelihood} testId="lt-fling" />
          <ScoreBar label={t("ghostRisk")} value={lt.ghost_risk} invert testId="lt-ghost" />
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[#dcdde6]">{lt.reasoning}</p>
        <KV label={t("timeline")} value={lt.timeline} block />
      </Section>

      {/* Strategy */}
      <Section icon={<MessageSquare className="h-5 w-5 text-[#ff4458]" />} title={t("approachStrategy")} testId="strategy-section">
        <KV label={t("openerMessage")} value={s.opener_message} block highlight />
        <KV label={t("firstDateIdea")} value={s.first_date_idea} block />
        <KV label={t("pace")} value={s.pace} block />
        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-[#a8adbd]">{t("topicsExplore")}</div>
            <List items={s.topics_to_explore} tone="good" />
          </div>
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-[#a8adbd]">{t("topicsAvoid")}</div>
            <List items={s.topics_to_avoid} tone="bad" />
          </div>
        </div>
        <div className="mt-4">
          <div className="mb-2 text-xs uppercase tracking-wider text-[#a8adbd]">{t("milestones")}</div>
          <List items={s.milestones} tone="neutral" />
        </div>
      </Section>

      {/* Diagnosis */}
      <Section icon={<Shield className="h-5 w-5 text-[#fdb022]" />} title={t("diagnosis")} testId="premium-diagnosis">
        <p className="text-sm leading-relaxed text-[#dcdde6]">{r.diagnosis}</p>
      </Section>
      <Section icon={<Crown className="h-5 w-5 text-[#fdb022]" />} title={t("finalRecommendation")} testId="final-recommendation" accent>
        <p className="text-sm leading-relaxed text-[#dcdde6]">{r.final_recommendation}</p>
      </Section>
    </div>
  );
}

function Section({ icon, title, children, testId, accent = false }) {
  return (
    <section data-testid={testId} className={`dc-card relative overflow-hidden p-6 sm:p-8 ${accent ? "ring-1 ring-[#ff4458]/30" : ""}`}>
      {accent && <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-gradient-to-br from-[#ff4458]/30 to-transparent blur-3xl" />}
      <div className="relative">
        <div className="mb-4 flex items-center gap-2">
          {icon}
          <h2 className="font-display text-lg font-semibold text-white">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}

function KV({ label, value, block = false, highlight = false }) {
  return (
    <div className={block ? "mt-3" : ""}>
      <div className="text-xs uppercase tracking-wider text-[#a8adbd]">{label}</div>
      <div className={`mt-1 text-sm leading-relaxed ${highlight ? "font-display text-lg font-semibold dc-gradient-text" : "text-white"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function List({ items, tone }) {
  if (!items || items.length === 0) return <p className="text-sm text-[#a8adbd]">—</p>;
  const dot = { good: "bg-[#32d583]", bad: "bg-[#fd267a]", neutral: "bg-[#fdb022]" }[tone] || "bg-white";
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-white">
          <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${dot}`} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
